// LT-295: opportunity_source_of_truth toggle. Admin-only; changing the
// setting shifts ownership semantics for Opportunity rows so we gate
// the change behind a confirm-modal. Member users see the current value
// in read-only form.
import React, { useState } from 'react';
import {
  CRMConnection,
  CRMOpportunitySource,
  asCrmApiError,
  crmSyncAPI,
} from '../services/crmSyncService';
import { APP_NAME } from '../config/region';

export interface CrmSourceOfTruthToggleProps {
  connection: CRMConnection;
  canEdit?: boolean;
  onUpdated?: (connection: CRMConnection) => void;
}

const LABELS: Record<CRMOpportunitySource, string> = {
  cold_caller: `${APP_NAME} (sequence engine owns stage progression)`,
  crm: 'CRM (your CRM is the source of truth)',
};

const CrmSourceOfTruthToggle: React.FC<CrmSourceOfTruthToggleProps> = ({
  connection,
  canEdit = true,
  onUpdated,
}) => {
  const [pending, setPending] = useState<CRMOpportunitySource | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = connection.opportunity_source_of_truth;

  const cancel = () => {
    setPending(null);
    setError(null);
  };

  const commit = async () => {
    if (pending === null) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await crmSyncAPI.updateConnectionSettings({
        opportunity_source_of_truth: pending,
      });
      setPending(null);
      onUpdated?.(updated);
    } catch (e) {
      setError(asCrmApiError(e).message || 'Update failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="crm-source-of-truth-toggle"
      className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-lg shadow p-4 mb-6"
    >
      <h2 className="text-white text-base font-medium mb-2">
        Opportunity source of truth
      </h2>
      <p className="text-xs text-gray-400 mb-3">
        Decides which side wins on opportunity pulls. Activities are always
        push-only; contacts and accounts always defer to the CRM for identity
        fields. This toggle only affects opportunities.
      </p>

      {error && (
        <div
          role="alert"
          className="mb-3 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200"
        >
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-300" htmlFor="source-select">
          Current
        </label>
        <select
          id="source-select"
          disabled={!canEdit || submitting}
          value={pending ?? current}
          onChange={(e) =>
            setPending(e.target.value as CRMOpportunitySource)
          }
          className="bg-slate-900/60 border border-white/10 rounded px-2 py-1 text-sm text-gray-200 disabled:opacity-50"
        >
          <option value="cold_caller">{LABELS.cold_caller}</option>
          <option value="crm">{LABELS.crm}</option>
        </select>
      </div>

      {pending !== null && pending !== current && canEdit && (
        <div
          role="dialog"
          aria-label="Confirm source-of-truth change"
          className="mt-4 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-3 text-sm text-amber-100"
        >
          <div className="mb-2">
            Changing ownership from <strong>{LABELS[current]}</strong> to{' '}
            <strong>{LABELS[pending]}</strong>. Future opportunity pulls will{' '}
            {pending === 'crm'
              ? 'overwrite local opportunity fields when they differ from the CRM.'
              : `be ignored — ${APP_NAME} becomes authoritative.`}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={submitting}
              onClick={commit}
              className="px-3 py-1.5 bg-amber-600 text-white text-xs rounded hover:bg-amber-700 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Confirm change'}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={cancel}
              className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CrmSourceOfTruthToggle;
